/**
 * FormPage — a form as an independent, savable webpage (TRUS-545 Phase 1).
 *
 * Entering a progress note took seven steps: Members & Cases -> the member ->
 * the Program Activities workspace -> an enrollment picker -> the Forms
 * work-tab -> EnrollmentDetailNew -> FormLibraryNew -> the template -> the
 * editor. This route collapses that to one URL.
 *
 *   /forms/instance/{instanceId}     open an existing instance
 *   /forms/new/{templateId}?member_id=&program_id=&enrollment_id=
 *
 * FormInstanceEditor is already a full-page surface — FormLibraryNew and
 * MyFormsInbox both return it outright, replacing the list — so this page is
 * routing, context and a gate, not a second editor. Everything the editor needs
 * is a prop, and a standalone route has no React context from the profile it
 * was opened from, so the member context travels in the URL. That is also what
 * makes the link shareable and bookmarkable.
 *
 * GATING: the standalone preview route at /forms/preview/:id is gated on
 * `isAuthenticated` alone — no capability, no page access. It is read-only, so
 * the exposure is small. This page SAVES, and is addressed by member id, so the
 * same gate would let any signed-in user in the org open a progress note
 * against any member by typing a URL. It checks `forms.library` — the page key
 * the sidebar's "Forms & consents" entry already resolves — via
 * usePageCapability, which reports hasAccess=false until proven otherwise.
 *
 * That check is a UX boundary, not a security one, and deliberately so: the
 * org boundary is enforced server-side (every forms query is scoped by
 * cbo_id), but POST /forms/instances/ carries NO role check today, so any
 * authenticated user in the org can already create an instance through the
 * API. That gap predates this route and is not widened by it — closing it
 * would 403 roles that create forms today, which is a product call.
 *
 * NB: components/common/PageGate.tsx looks like the right wrapper and is not —
 * it has no consumers anywhere and imports next/navigation into what is a Vite
 * app, so it is never bundled and has never run. Use the hook directly.
 */

import React from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import { FormInstanceEditor } from '@/components/forms/FormInstanceEditorByOrg';
import { usePageCapability } from '@/hooks/usePageCapability';
import { useAppTranslation } from '@/contexts/TranslationContext';

const FORMS_PAGE_KEY = 'forms.library';

export interface FormPageProps {
  /** Open an existing instance. Mutually exclusive with templateId. */
  instanceId?: number;
  /** Start a new instance from this template. */
  templateId?: number;
  memberId?: number;
  programId?: number;
  enrollmentId?: number;
}

/**
 * Parse /forms/instance/{id} and /forms/new/{templateId}?member_id=... .
 * Exported so the route contract can be tested without a router.
 */
export function parseFormPagePath(pathname: string, search: string): FormPageProps | null {
  const num = (raw: string | null | undefined): number | undefined => {
    if (!raw) return undefined;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };

  const params = new URLSearchParams(search || '');
  const context = {
    memberId: num(params.get('member_id')),
    programId: num(params.get('program_id')),
    enrollmentId: num(params.get('enrollment_id')),
  };

  if (pathname.startsWith('/forms/instance/')) {
    const instanceId = num(pathname.split('/forms/instance/')[1]?.split('/')[0]);
    return instanceId ? { instanceId, ...context } : null;
  }

  if (pathname.startsWith('/forms/new/')) {
    const templateId = num(pathname.split('/forms/new/')[1]?.split('/')[0]);
    // A new instance has to be created against something. Without a member the
    // editor would save an orphan, so treat it as an unroutable URL rather than
    // opening an editor that cannot legally save.
    if (!templateId || !context.memberId) return null;
    return { templateId, ...context };
  }

  return null;
}

/** Build the URL for a form opened from a member's context. */
export function formPageUrl(args: {
  instanceId?: number;
  templateId?: number;
  memberId?: number;
  programId?: number;
  enrollmentId?: number;
}): string {
  const query = new URLSearchParams();
  if (args.memberId) query.set('member_id', String(args.memberId));
  if (args.programId) query.set('program_id', String(args.programId));
  if (args.enrollmentId) query.set('enrollment_id', String(args.enrollmentId));
  const suffix = query.toString() ? `?${query.toString()}` : '';

  return args.instanceId
    ? `/forms/instance/${args.instanceId}${suffix}`
    : `/forms/new/${args.templateId}${suffix}`;
}

export default function FormPage({
  instanceId,
  templateId,
  memberId,
  programId,
  enrollmentId,
}: FormPageProps) {
  const { t } = useAppTranslation();
  const organizationId = React.useMemo(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }, []);
  const { hasAccess, loading } = usePageCapability(organizationId, FORMS_PAGE_KEY);

  // Leaving on the page means closing the tab, so send the user back to the
  // app rather than to a blank screen. history.back() keeps a deep link opened
  // in the same tab behaving like a normal in-app navigation.
  const close = () => {
    if (window.history.length > 1) window.history.back();
    else window.location.assign('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {t('forms.noAccessTitle', 'You do not have access to this form')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t(
              'forms.noAccessBody',
              'Ask an administrator to grant your role access to Forms & consents.'
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto p-4">
        <FormInstanceEditor
          instanceId={instanceId}
          templateId={templateId}
          memberId={memberId}
          programId={programId}
          enrollmentId={enrollmentId}
          onClose={close}
          onSubmit={close}
        />
      </div>
    </div>
  );
}
